import { Text } from '_/components';
import { colors, spacing } from '_/constants/theme';
import { Camera, CameraView, BarcodeScanningResult } from 'expo-camera';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { getBottomSpace } from 'react-native-iphone-x-helper';
import Svg, { Path } from 'react-native-svg';
import QRCodeReaderWeb from 'react-qr-reader';

interface Props {
  onScanned: (data: string) => Promise<void> | void;
  onClose: () => void;
  loading?: boolean;
}

export default function QRCameraReader({ onScanned, onClose, loading = false }: Props) {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [scanned, setScanned] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (Platform.OS === 'web') {
      setHasPermission(true);
      return;
    }

    (async () => {
      const { status } = await Camera.requestCameraPermissionsAsync();
      setHasPermission(status === 'granted');
    })();
  }, []);

  const handleBarCodeScanned = useCallback(
    async (result: BarcodeScanningResult | string | null) => {
      if (!result || scanned || loading) return;
      setScanned(true);
      const data = typeof result === 'string' ? result : result.data;
      await onScanned(data);
    },
    [scanned, loading, onScanned]
  );

  const Scanner = useCallback(() => {
    if (Platform.OS === 'web') {
      return (
        <QRCodeReaderWeb
          onError={() => null}
          showViewFinder={false}
          delay={300}
          onScan={handleBarCodeScanned}
          style={StyleSheet.absoluteFillObject}
        />
      );
    }

    return (
      <CameraView
        facing="back"
        barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
        onBarcodeScanned={scanned || loading ? undefined : handleBarCodeScanned}
        style={StyleSheet.absoluteFillObject}
      />
    );
  }, [scanned, loading, handleBarCodeScanned]);

  return (
    <View style={styles.container}>
      <View style={styles.cameraContainer}>
        {hasPermission && <Scanner />}
        <View style={styles.layerTop}>
          {hasPermission && (
            <View style={styles.titleContainer}>
              <Text center fontType="bold" fontSize={28} color={colors.white} style={styles.title}>
                {t('qrCodeScreen.qrCode')}
              </Text>
              <Text center fontSize={16} color={colors.white}>
                {t('qrCodeScreen.qrCodeMessage')}
              </Text>
            </View>
          )}
          {hasPermission === null && (
            <View style={styles.titleContainer}>
              <Text fontType="bold" center fontSize={28} color={colors.white} style={styles.title}>
                {t('qrCodeScreen.waitingCameraRequest')}
              </Text>
            </View>
          )}
          {hasPermission === false && (
            <View style={styles.titleContainer}>
              <Text fontType="bold" center fontSize={28} color={colors.white} style={styles.title}>
                {t('qrCodeScreen.noAccessToCamera')}
              </Text>
              <Text center fontSize={16} color={colors.white}>
                {t('qrCodeScreen.openConfigToAllow')}
              </Text>
            </View>
          )}
        </View>

        <View style={styles.layerCenter}>
          <View style={styles.layerLeft} />
          <Svg width="280" height="280" viewBox="0 0 280 280" fill="none">
            <Path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 0H0V12C0 5.37262 5.37256 0 12 0ZM268 0C274.627 0 280 5.37256 280 12V0H268ZM280 268C280 274.627 274.627 280 268 280H280V268ZM12 280C5.37256 280 0 274.627 0 268V280H12Z"
              fill={colors.black}
              fillOpacity="0.75"
            />
            <Path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M26 20C22.6863 20 20 22.6863 20 26V50L22.8977 28.0604C23.253 25.37 25.37 23.253 28.0604 22.8977L50 20H26ZM20 254C20 257.314 22.6863 260 26 260H50L28.0604 257.102C25.37 256.747 23.253 254.63 22.8977 251.94L20 230V254ZM254 20C257.314 20 260 22.6863 260 26V50L257.102 28.0604C256.747 25.37 254.63 23.253 251.94 22.8977L230 20H254ZM254 260C257.314 260 260 257.314 260 254V230L257.102 251.94C256.747 254.63 254.63 256.747 251.94 257.102L230 260H254Z"
              fill="white"
              fillOpacity="0.5"
            />
          </Svg>
          <View style={styles.layerRight} />
        </View>

        <View style={styles.layerBottom} />
      </View>

      <TouchableOpacity onPress={onClose} style={styles.button}>
        <Text center fontSize={18} fontType="bold" color={colors.clear}>
          {t('qrCodeScreen.closeReader')}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

const opacity = 0.75;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      width: 550,
      alignSelf: 'center',
    }),
  },
  cameraContainer: {
    flex: 1,
    backgroundColor: colors.black,
  },
  titleContainer: {
    marginTop: 12,
    alignItems: 'center',
  },
  title: {
    marginTop: 48,
    marginBottom: 6,
  },
  layerTop: {
    flex: 2,
    backgroundColor: colors.black,
    alignItems: 'center',
    justifyContent: 'center',
    opacity,
  },
  layerCenter: {
    flexDirection: 'row',
  },
  layerLeft: {
    flex: 1,
    backgroundColor: colors.black,
    opacity,
  },
  layerRight: {
    flex: 1,
    backgroundColor: colors.black,
    opacity,
  },
  layerBottom: {
    flex: 2,
    backgroundColor: colors.black,
    opacity,
  },
  button: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.black,
    paddingTop: spacing.lg,
    paddingBottom: getBottomSpace() + spacing.lg,
  },
});
